import React from "react";
import { Badge } from "@/components/ui/badge";

export interface SecondaryMetricItem {
  key?: string;
  label: string;
  value: string | number;
  count?: number;
  badge?: string;
  note?: string;
  onClick?: () => void;
  variant?: "default" | "emerald" | "rose" | "amber" | "outline";
}

export interface FinanceSecondaryMetricsProps {
  title?: string;
  metrics: SecondaryMetricItem[];
  className?: string;
}

export function FinanceSecondaryMetrics({
  title = "Métricas secundarias",
  metrics,
  className = "",
}: FinanceSecondaryMetricsProps) {
  if (!metrics || metrics.length === 0) return null;

  const badgeClassByVariant: Record<NonNullable<SecondaryMetricItem["variant"]>, string> = {
    default: "",
    outline: "",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300",
    amber: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-300",
  };

  return (
    <div className={`rounded-lg border border-border/60 bg-muted/20 p-3 ${className}`}>
      {title && (
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          {title}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {metrics.map((metric, index) => {
          const isClickable = Boolean(metric.onClick);
          const Component = isClickable ? "button" : "div";

          return (
            <Component
              key={metric.key || metric.label || index}
              onClick={metric.onClick}
              type={isClickable ? "button" : undefined}
              className={`flex items-center gap-2 rounded-md border bg-background px-2.5 py-1 text-xs shadow-2xs transition-all ${
                isClickable
                  ? "hover:border-primary/50 hover:bg-accent cursor-pointer text-left focus:outline-none focus:ring-1 focus:ring-ring"
                  : "border-border/80"
              }`}
            >
              <span className="font-normal text-muted-foreground">{metric.label}:</span>
              <span className="font-semibold tabular-nums text-foreground">{metric.value}</span>

              {metric.count !== undefined && (
                <span className="text-[11px] text-muted-foreground">({metric.count})</span>
              )}

              {metric.badge && (
                <Badge
                  variant={metric.variant === "outline" ? "outline" : "secondary"}
                  className={`h-4 px-1.5 py-0 text-[10px] ${badgeClassByVariant[metric.variant || "default"]}`}
                >
                  {metric.badge}
                </Badge>
              )}
            </Component>
          );
        })}
      </div>
    </div>
  );
}
